import { memo, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { Badge2 } from "@/ui2/atoms/Badge";
import { Cell2 } from "@/ui2/atoms/(table)/Cell";
import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";
import { Menu2 } from "@/ui2/molecules/Menu";
import { Row2 } from "@/ui2/molecules/(table)/Row";
import UrgencyIcon from "@/assets/images/icons2/icon--error.svg?component";

import { ACTION_ITEMS_BY_STATUS, URGENCY_THRESHOLD } from "../constants";
import type { AdaptedQueueRow } from "../helpers/adapters";
import { NoteAction } from "../../types";
import { STATUS_VARIANT } from "../../constants";
import { useCosignatureModal } from "../../CosignatureModalProvider";

type CosignatureTableRowProps = {
  row: AdaptedQueueRow;
};

function CosignatureTableRowComponent({ row }: CosignatureTableRowProps) {
  const history = useHistory();
  const { openModal } = useCosignatureModal();

  const handleActionClick = useCallback(
    (key: NoteAction) => {
      switch (key) {
        case "cosign":

        case "reject":
          openModal({ action: key, row });
          break;

        case "review":
          history.push(`/app/patient/${row.patientId}/note/${row.id}/v2`);
          break;

        default:
          key satisfies never;
      }
    },
    [history, openModal, row],
  );

  const items = ACTION_ITEMS_BY_STATUS[row.status];

  return (
    <Row2 id={row.id}>
      <Cell2>{row.submittedBy}</Cell2>
      <Cell2>{row.patientName}</Cell2>
      <Cell2>{row.type}</Cell2>

      <Cell2 className="tw:py-2.5">
        <time
          dateTime={row.signatureDate ? dayjs(row.signatureDate).format("YYYY-MM-DD") : undefined}
        >
          {row.dateSigned}
        </time>

        <Countdown row={row} />
      </Cell2>

      <Cell2>
        <Badge2
          appearance={STATUS_VARIANT[row.status].appearance}
          className="tw:capitalize"
          size="xs"
          variant={STATUS_VARIANT[row.status].variant}
        >
          {row.status}
        </Badge2>
      </Cell2>

      <Cell2 className="tw:py-3">
        <Menu2
          classNameTrigger="tw:align-middle"
          items={items}
          onAction={handleActionClick}
          placement="left top"
        />
      </Cell2>
    </Row2>
  );
}

export const CosignatureTableRow = memo(CosignatureTableRowComponent);

function Countdown({ row }: { row: AdaptedQueueRow }) {
  const { daysPending, status } = row;

  const isActionable = ["pending", "resubmitted"].includes(status);
  const isOverdue = daysPending !== null && daysPending > URGENCY_THRESHOLD;
  const isUrgent = isActionable && isOverdue;
  const isCountdownShown = isActionable && daysPending !== null && daysPending > 0;

  return (
    <span
      className={cn(
        "tw:flex tw:items-center tw:gap-1 tw:text-sm tw:leading-none tw:text-text-tertiary",
        isUrgent && "tw:text-text-error!",
      )}
    >
      {isUrgent ? <UrgencyIcon className="tw:size-5 tw:text-text-error" /> : null}
      {isCountdownShown ? `${daysPending}d` : null}
    </span>
  );
}
